import React, { useEffect, useState, lazy } from "react";
import { Carousel } from "../../components/carousel";
import { CategoryGrid } from "../../components/category-grid";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchCategories,
  fetchSubCategories,
} from "../../store/slices/category-slice";
import { AppDispatch, RootState } from "../../store/store";
import { fetchPromoImages } from "../../store/slices/promo-slice";
import {
  fetchAllProducts,
  fetchSpecialProducts,
} from "../../store/slices/product-slice";
import SellexProductContainer from "../../components/sellexx-product-container";
import SellexxLogo from "../../components/sellexx-logo";
import { ClipLoader } from "react-spinners";
import NavigationBar from "../../components/home-navigation";
import { useNavigate } from "react-router-dom";
import Footer from "../../components/footer";

// Lazy load non-critical components
const LazyFeatureGrid = lazy(() =>
  import("../../components/feature-grid").then((module) => ({
    default: module.FeatureGrid,
  }))
);

const SellexxWebHome = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { images, loading: promoLoading } = useSelector(
    (state: RootState) => state.promo
  );

  const {
    items: products,
    loading: productsLoading,
    specialProducts,
    specialProductsLoading,
  } = useSelector((state: RootState) => state.products);

  const {
    items: categories,
    subcategories,
    loading: categoriesLoading,
  } = useSelector((state: RootState) => state.categories);

  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const [lastScrollPosition, setLastScrollPosition] = useState(0);
  const [selectedCurrency, setSelectedCurrency] = useState("SAR");
  const navigate = useNavigate();

  /// Optimized Header Control with debouncing
  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const currentScrollPosition = window.scrollY;

          if (
            currentScrollPosition > lastScrollPosition &&
            currentScrollPosition > 50
          ) {
            setIsHeaderVisible(false);
          } else {
            setIsHeaderVisible(true);
          }

          setLastScrollPosition(currentScrollPosition);
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollPosition]);

  // ✅ Phase 1: Fetch ONLY critical content on initial mount
  useEffect(() => {
    // Fire-and-forget these critical dispatches.
    // The UI will update automatically via useSelector when they are done.
    if (!(images?.length > 0)) {
      dispatch(fetchPromoImages());
    }
    if (!(specialProducts?.items?.length > 0)) {
      dispatch(fetchSpecialProducts());
    }
  }, [dispatch, images?.length, specialProducts?.items?.length]);

  // ✅ Phase 2: Fetch non-critical content AFTER critical content has loaded
  useEffect(() => {
    // This effect runs only when the critical data (images and special products) exists.
    const criticalContentLoaded =
      images.length > 0 && specialProducts.items.length > 0;

    if (criticalContentLoaded) {
      // Now fetch everything else
      if (!(categories?.length > 0)) {
        dispatch(fetchCategories());
      }
      if (!(subcategories?.length > 0)) {
        dispatch(fetchSubCategories());
      }
      if (!(products?.length > 0)) {
        dispatch(fetchAllProducts());
      }
    }
    // This dependency array ensures this hook runs once the critical data is available.
  }, [
    dispatch,
    images,
    specialProducts,
    categories?.length,
    subcategories?.length,
    products?.length,
  ]);



  const handleCategoryTap = (categoryId: string) => {
    if (categoryId.trim()) {
      navigate(`/category?categoryId=${categoryId}`);
    }
  };

  const handleSubCategoryTap = (subCategoryId: string) => {
    if (subCategoryId.trim()) {
      navigate(`/category?subcategoryId=${subCategoryId}`);
    }
  };

  return (
    <div className="min-h-screen bg-primary-green">
      {/* Desktop Header */}
      <header
        className={`hidden lg:flex fixed top-0 lg:z-50 w-full bg-primary-grey items-center justify-between py-4 md:px-8 transition-transform duration-300 ${
          isHeaderVisible ? "translate-y-0" : "-translate-y-24"
        }`}
      >
        <div className="navbar-parent">
          <NavigationBar
            categories={categories}
            subcategories={subcategories}
            onCurrencyChange={setSelectedCurrency}
          />
        </div>
      </header>

      {/* Mobile Headers */}
      <header
        className={`lg:hidden fixed top-0 z-50 w-full bg-primary-grey flex flex-col items-center justify-center py-4 md:px-8 transition-transform duration-300 ${
          isHeaderVisible ? "translate-y-0" : "-translate-y-full"
        }`}
      >
        <SellexxLogo withText className="h-11 lg:h-16" />
      </header>
      <header className="lg:hidden w-full bg-primary-grey items-center justify-center py-4 shadow-2xl md:px-8">
        <NavigationBar categories={categories} subcategories={subcategories} />
      </header>

      {/* Main Content */}
      <main className="pb-8">
        
        <>
          {/* Hero Carousel - Critical Content */}
          <section className="relative mb-8">
            <Carousel slides={images} />
          </section>

          {/* Special Products Section - Critical Content */}
          {specialProductsLoading ? (
            <div className="flex justify-center items-center py-8">
              <ClipLoader size={30} color={"#4A90E2"} />
              <span className="ml-2 text-gray-600">Loading specials...</span>
            </div>
          ) : (
           specialProducts.label && specialProducts.items.length > 0 && (
            <div className="max-w-7xl mx-auto px-4 mb-12">
              <section>
                <h2 className="relative mb-12 text-center text-3xl font-bold text-gray-800 md:text-4xl lg:text-5xl">
                  {specialProducts.label}
                  <span className="absolute -bottom-6 left-1/2 h-1 w-24 -translate-x-1/2 transform rounded-full bg-gray-500" />
                </h2>
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                  {specialProducts.items.map((product) => (
                    <SellexProductContainer
                      key={product.id}
                      product={product}
                      selectedCurrency={selectedCurrency}
                    />
                  ))}
                </div>
              </section>
            </div>
            )
          )}

       
         

          <div className="max-w-7xl mx-auto px-4 space-y-12">
            {/* Main Categories Section - Phase 2 */}
          
              <>
                {categoriesLoading ? (
                  <div className="flex justify-center items-center py-8">
                    <ClipLoader size={30} color={"#4A90E2"} />
                    <span className="ml-2 text-gray-600">
                      Loading categories...
                    </span>
                  </div>
                ) : categories.length > 0 ? (
                  <CategoryGrid
                    categoryTitle="Browse by Category"
                    categories={categories}
                    alternateStyle={true}
                    bypassLabel={true}
                    onCategoryClick={(val) => handleCategoryTap(val.id)}
                  />
                ) : null}
              </>
        

            {/* Products section - Phase 3 */}
        
              <section>
                <h2 className="relative mb-12 text-center text-3xl font-bold text-gray-800 md:text-4xl lg:text-5xl">
                  Featured Products
                  <span className="absolute -bottom-6 left-1/2 h-1 w-24 -translate-x-1/2 transform rounded-full bg-gray-500" />
                </h2>
                {productsLoading ? (
                  <div className="flex justify-center items-center py-8">
                    <ClipLoader size={30} color={"#4A90E2"} />
                    <span className="ml-2 text-gray-600">
                      Loading products...
                    </span>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
                    {products.map((product) => (
                      <SellexProductContainer
                        key={product.id}
                        product={product}
                        selectedCurrency={selectedCurrency}
                      />
                    ))}
                  </div>
                )}
              </section>
      

            {/* Subcategories - Phase 3 */}
        
              <CategoryGrid
                categoryTitle="Browse by Type"
                categories={subcategories}
                isSquare={true}
                onCategoryClick={(val) => handleSubCategoryTap(val.id)}
              />
    
          </div>
        </>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default SellexxWebHome;
